import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { Message } from '@chatapp-inbox/shared';

/** Até esta distância do fim a pessoa conta como "acompanhando o fim" da conversa. */
export const NEAR_BOTTOM_PX = 80;

/** Id do elemento de cada mensagem, de onde sai a posição usada para compensar a rolagem. */
export const messageElementId = (key: string): string => `message-${key}`;

/**
 * Contador fora do React (lido com `useSyncExternalStore`): a rolagem decide em efeito de layout,
 * antes da pintura, se há mensagens novas fora da tela, e atualizar estado ali dentro causaria
 * uma renderização a mais por quadro.
 */
function createCounter() {
  let value = 0;
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((listener) => listener());
  return {
    get: (): number => value,
    add(amount: number): void {
      value += amount;
      emit();
    },
    clear(): void {
      if (value === 0) return;
      value = 0;
      emit();
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

interface Snapshot {
  firstId: string | null;
  lastId: string | null;
  /** Posição (desde o topo do conteúdo) da mensagem mais antiga que estava na tela. */
  firstTop: number;
}

export interface ChatScroll {
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  /** Mensagens novas que chegaram enquanto a pessoa lia o histórico (0 = nenhuma). */
  unseen: number;
  /** Vai ao fim e passa a acompanhá-lo (botão "Novas mensagens" e envio próprio). */
  stickToBottom: () => void;
}

/** Posição do elemento desde o topo do conteúdo rolável; null se ele não estiver na tela. */
function positionOf(scroller: HTMLElement, key: string | null): number | null {
  if (key === null) return null;
  const element = scroller.ownerDocument.getElementById(messageElementId(key));
  if (!element || !scroller.contains(element)) return null;
  return (
    element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
  );
}

/**
 * A rolagem da conversa.
 *
 * - Ao abrir, vai ao fim ANTES da pintura (efeito de layout: sem quadro com o topo à vista).
 * - Acompanha o fim enquanto a pessoa está a até {@link NEAR_BOTTOM_PX} dele; se ela subiu para
 *   ler, não rola sozinha e conta as mensagens novas (o botão "Novas mensagens").
 * - Quando páginas mais antigas entram no topo, devolve a rolagem para que a mensagem que estava à
 *   vista continue no mesmo lugar da tela.
 * - Se a área rolável muda de altura (teclado do celular, aviso, compositor que cresce) ou some e
 *   volta (`display: none` ao ir para os detalhes, que ZERA a rolagem), mantém o fim ou restaura
 *   a distância que a pessoa tinha dele.
 *
 * `messages` são as mensagens do SERVIDOR em ordem cronológica; `entries` muda a cada alteração do
 * que está na tela (inclusive envios locais), para o fim ser mantido também nesses casos.
 */
export function useChatScroll(params: {
  messages: readonly Message[];
  entries: readonly unknown[];
  viewerId: string;
}): ChatScroll {
  const { messages, entries, viewerId } = params;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const stuck = useRef(true);
  const distance = useRef(0);
  const snapshot = useRef<Snapshot | null>(null);
  const [counter] = useState(createCounter);
  const unseen = useSyncExternalStore(counter.subscribe, counter.get, counter.get);

  const jumpToBottom = useCallback((element: HTMLElement): void => {
    element.scrollTop = element.scrollHeight;
    stuck.current = true;
    distance.current = 0;
  }, []);

  useLayoutEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const firstId = messages[0]?.id ?? null;
    const lastId = messages.at(-1)?.id ?? null;
    const previous = snapshot.current;

    if (!previous) {
      jumpToBottom(element);
    } else {
      const olderArrived =
        previous.firstId !== null &&
        firstId !== previous.firstId &&
        messages.some((message) => message.id === previous.firstId);
      if (olderArrived) {
        if (stuck.current) {
          jumpToBottom(element);
        } else {
          const top = positionOf(element, previous.firstId);
          if (top !== null) element.scrollTop += top - previous.firstTop;
        }
      }

      let appended: readonly Message[] = [];
      if (previous.lastId === null) {
        appended = messages;
      } else {
        const index = messages.findIndex((message) => message.id === previous.lastId);
        if (index >= 0) appended = messages.slice(index + 1);
      }
      if (stuck.current) {
        // Qualquer mudança no fim (mensagem nova, envio local, estado, aviso) mantém o fim à vista.
        jumpToBottom(element);
      } else {
        // O que a própria pessoa enviou (por outro aparelho, por exemplo) não precisa de aviso.
        const fresh = appended.filter(
          (message) => !(message.direction === 'out' && message.authorUserId === viewerId),
        );
        if (fresh.length > 0) counter.add(fresh.length);
      }
    }

    snapshot.current = { firstId, lastId, firstTop: positionOf(element, firstId) ?? 0 };
  }, [messages, entries, viewerId, counter, jumpToBottom]);

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let lastHeight = element.clientHeight;
    const observer = new ResizeObserver(() => {
      const height = element.clientHeight;
      const wasHidden = lastHeight === 0;
      lastHeight = height;
      if (height === 0) return;
      if (wasHidden) {
        // Voltou de `display: none`: o navegador zerou a rolagem. Restaura onde a pessoa estava.
        element.scrollTop = Math.max(0, element.scrollHeight - height - distance.current);
      } else if (stuck.current) {
        element.scrollTop = element.scrollHeight;
      }
    });
    observer.observe(element);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  const onScroll = useCallback((): void => {
    const element = scrollerRef.current;
    // Escondida (`display: none`) o navegador zera as medidas e a rolagem: não vale como leitura.
    if (!element || element.clientHeight === 0) return;
    const fromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    distance.current = fromBottom;
    stuck.current = fromBottom <= NEAR_BOTTOM_PX;
    if (stuck.current) counter.clear();
  }, [counter]);

  const stickToBottom = useCallback((): void => {
    const element = scrollerRef.current;
    stuck.current = true;
    distance.current = 0;
    counter.clear();
    if (element) element.scrollTop = element.scrollHeight;
  }, [counter]);

  return { scrollerRef, contentRef, onScroll, unseen, stickToBottom };
}
